/*
 *   File : add-ams-form.js
 *   Author URI : https://evoqueinnovativelab.com
 *   Description : Create AMS modal (custom) — search & select baskets, then
 *                 attach them to a dynamically created AMS. Unlike the Core AMS
 *                 form, no baskets are pre-loaded: the admin searches across all
 *                 baskets and picks one or more.
 *   Integrations : null
 *   Version : v1.0
 *   Created : 06-24-2026
 */

/* import packages */
import React, { useEffect, useMemo, useRef, useState } from "react";
import $ from "jquery";

/* import styles */
import styles from "../../styles/components/manageAMS.module.scss";

/* import components */
import SelectBox from "../dropdown/selectBox";
import { Toast } from "../note";

/* import assets */
import AddNew from "../../assets/images/dataFormating/add-new.svg";
import DeleteIcon from "../../assets/images/manage-content/Delete.svg";

/* import services */
import APIGet from "../../services/getApi";
import devconfig from "../../config/config";

const MODAL_ID = "addAMSForm";

export default function AddAMSForm(props) {

    const isEdit = Boolean(props.data?.id);

    /* full basket list — all baskets from /ams/basket-filter (replaces the
     * Core AMS /ams/filter list). */
    const [allBaskets, setAllBaskets] = useState([]);

    const [name, setName] = useState("");
    const [amsAlias, setAmsAlias] = useState("");
    /* Each row is one basket + its allocation %. Rows carry a stable local key
     * so blank/duplicate rows stay distinct for React and for edits. */
    const rowKeyRef = useRef(0);
    const _newRow = (basketId = null, allocation = "0") =>
        ({ key: rowKeyRef.current++, basketId, allocation });
    const [rows, setRows] = useState(() => [_newRow()]);
    const [minSipInput, setMinSipInput] = useState("");
    const [minLumpsumInput, setMinLumpsumInput] = useState("");
    const [minInvestMoreInput, setMinInvestMoreInput] = useState("");
    /* Dirty = admin has typed in the field; once dirty we stop auto-filling the
     * calculated minimum so the manual override survives (same as Core AMS). */
    const [minSipDirty, setMinSipDirty] = useState(false);
    const [minLumpsumDirty, setMinLumpsumDirty] = useState(false);
    const [minInvestMoreDirty, setMinInvestMoreDirty] = useState(false);
    const [loader, setLoader] = useState(false);
    const [error, setError] = useState(null);

    /* Pre-fill form when editing an existing custom AMS. */
    useEffect(() => {
        if (!isEdit || !props.data) return;

        setName(props.data.name ?? "");
        setAmsAlias(props.data.ams_alias ?? "");

        const incoming = Array.isArray(props.data.allocations) ? props.data.allocations : [];
        setRows(incoming.length
            ? incoming.map((a) => _newRow(a.goal_id, String(a.allocation ?? 0)))
            : [_newRow()]);

        /* SIP / Lumpsum use the same basket-derived auto-fill as create (dirty
         * stays false so calculated minimums populate the inputs). */
        setMinSipDirty(false);
        setMinLumpsumDirty(false);
        setMinSipInput("");
        setMinLumpsumInput("");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEdit, props.data]);

    /* clear state and remove backdrop when modal closes */
    useEffect(() => {
        const modalEl = document.getElementById(MODAL_ID);
        if (!modalEl) return;
        const handler = () => {
            setLoader(false);
            setError(null);
            $(".modal-backdrop").remove();
            $("body").removeAttr("style");
            if (props.closeModal) props.closeModal();
        };
        modalEl.addEventListener("hidden.bs.modal", handler);
        return () => modalEl.removeEventListener("hidden.bs.modal", handler);
    }, [props]);

    /* Load all baskets for the search dropdown from /ams/basket-filter.
     * Normalize id/name so the existing option/row/min logic keeps working. */
    useEffect(() => {
        const url = devconfig.admin + "/ams/basket-filter";
        APIGet(url).then((response) => {
            /* baskets live under data.basket_list; tolerate a bare data array
             * or a bare array as fallbacks. */
            const data = response?.data;
            const list = Array.isArray(data?.basket_list) ? data.basket_list
                : Array.isArray(data) ? data
                    : Array.isArray(response) ? response
                        : [];
            setAllBaskets(list.map((item) => ({
                ...item,
                id: item.id ?? item.value,
                name: item.name ?? item.label,
            })));
        }).catch(() => setAllBaskets([]));
    }, []);

    /* Resolve a basket id to its full object. Falls back to the edit payload's
     * allocations (goal_name + minimums) so an edited basket still resolves
     * before/without the /ams/basket-filter list. */
    const _basketFor = (basketId) => {
        const b = allBaskets.find((x) => Number(x.id) === Number(basketId));
        if (b) return b;
        if (isEdit && Array.isArray(props.data?.allocations)) {
            const fromEdit = props.data.allocations.find((a) => Number(a.goal_id) === Number(basketId));
            if (fromEdit) {
                return {
                    id: fromEdit.goal_id,
                    name: fromEdit.goal_name,
                    min_sip_amount: fromEdit.min_sip_amount ?? 0,
                    min_lumpsum_amount: fromEdit.min_lumpsum_amount ?? 0,
                    min_invest_more_amount: fromEdit.min_invest_more_amount ?? 0,
                };
            }
        }
        return null;
    };

    /* Options for a given row's dropdown — search works across ALL baskets.
     * Baskets already chosen in the OTHER rows are filtered out so the same
     * basket can't be picked twice. */
    const _optionsForRow = (rowKey) => {
        const taken = rows
            .filter((r) => r.key !== rowKey && r.basketId != null)
            .map((r) => Number(r.basketId));
        return allBaskets
            .filter((b) => !taken.includes(Number(b.id)))
            .map((b) => ({
                label: String(b.name ?? "").replace(/\n/g, " "),
                value: b.id,
            }));
    };

    /* Controlled single-select value for a row — resolved back to its option. */
    const _valueForRow = (row) => {
        const b = _basketFor(row.basketId);
        return b ? { label: String(b.name ?? "").replace(/\n/g, " "), value: b.id } : null;
    };

    /* rows that actually have a basket chosen — the real allocations */
    const selectedRows = useMemo(() => rows.filter((r) => r.basketId != null), [rows]);

    const _handleRowSelect = (rowKey, option) => {
        const basketId = option ? option.value : null;
        setRows((prev) => prev.map((r) => (r.key === rowKey ? { ...r, basketId } : r)));
    };

    const _handleAllocationChange = (rowKey, raw) => {
        const cleaned = raw.replace(/[^0-9.]/g, "");
        if (cleaned === "" || (Number(cleaned) >= 0 && Number(cleaned) <= 100)) {
            setRows((prev) => prev.map((r) => (r.key === rowKey ? { ...r, allocation: cleaned } : r)));
        }
    };

    const _addRow = () => setRows((prev) => [...prev, _newRow()]);

    /* Remove a row; always keep at least one (blank) row on screen. */
    const _removeRow = (rowKey) => {
        setRows((prev) => {
            const next = prev.filter((r) => r.key !== rowKey);
            return next.length ? next : [_newRow()];
        });
    };

    const totalAllocation = useMemo(() => selectedRows.reduce((acc, r) => {
        const v = parseFloat(r.allocation);
        return acc + (Number.isFinite(v) ? v : 0);
    }, 0), [selectedRows]);

    /* AMS minimum (same rule as Core AMS):
     *   required_for_basket = basket_min * 100 / allocation_pct
     *   ams_min = max(required_for_each_basket)
     * Skip baskets with 0% allocation. Math.ceil so the rounded amount still
     * satisfies the basket min. */
    const { minSipAmount, minLumpsumAmount, minInvestMoreAmount } = useMemo(() => {
        let sipMax = 0;
        let lumpsumMax = 0;
        let investMoreMax = 0;
        selectedRows.forEach((r) => {
            const b = _basketFor(r.basketId);
            if (!b) return;
            const pct = parseFloat(r.allocation);
            if (!Number.isFinite(pct) || pct <= 0) return;
            const sipMin = Number(b.min_sip_amount) || 0;
            const lumpMin = Number(b.min_lumpsum_amount) || 0;
            const investMoreMin = Number(b.min_invest_more_amount) || 0;
            const reqSip = Math.ceil((sipMin * 100) / pct);
            const reqLump = Math.ceil((lumpMin * 100) / pct);
            const reqInvestMore = Math.ceil((investMoreMin * 100) / pct);
            if (reqSip > sipMax) sipMax = reqSip;
            if (reqLump > lumpsumMax) lumpsumMax = reqLump;
            if (reqInvestMore > investMoreMax) investMoreMax = reqInvestMore;
        });
        return { minSipAmount: sipMax, minLumpsumAmount: lumpsumMax, minInvestMoreAmount: investMoreMax };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRows, allBaskets, isEdit, props.data]);

    /* Display the calculated minimum in the input fields, recomputed as baskets
     * are added/removed or allocations change. Once the admin edits a field its
     * dirty flag stops the auto-fill so the manual value survives. */
    useEffect(() => {
        if (minSipDirty) return;
        setMinSipInput(minSipAmount > 0 ? String(minSipAmount) : "");
    }, [minSipAmount, minSipDirty]);

    useEffect(() => {
        if (minLumpsumDirty) return;
        setMinLumpsumInput(minLumpsumAmount > 0 ? String(minLumpsumAmount) : "");
    }, [minLumpsumAmount, minLumpsumDirty]);

    useEffect(() => {
        if (minInvestMoreDirty) return;

        if (isEdit && props.data) {
            const saved = props.data.min_invest_more ?? props.data.min_invest_more_amount;
            if (saved != null && saved !== "") {
                setMinInvestMoreInput(String(saved));
                setMinInvestMoreDirty(true);
                return;
            }
        }

        setMinInvestMoreInput(minInvestMoreAmount > 0 ? String(minInvestMoreAmount) : "");
    }, [minInvestMoreAmount, minInvestMoreDirty, isEdit, props.data]);

    useEffect(() => {
        if (error !== null) {
            const t = setTimeout(() => setError(null), 10000);
            return () => clearTimeout(t);
        }
    }, [error]);

    /* Name and all minimum amounts are required — the submit button stays
     * disabled until they're filled (same as the name). AMS Alias is optional. */
    const allFilled = name.trim().length > 0
        && minSipInput.trim() !== ""
        && minLumpsumInput.trim() !== ""
        && minInvestMoreInput.trim() !== ""
        && selectedRows.length > 0
        && selectedRows.every((r) => r.allocation !== "" && r.allocation !== null && r.allocation !== undefined);

    const isValid = allFilled && Math.round(totalAllocation * 100) / 100 === 100;

    const _handleSubmit = () => {
        if (selectedRows.length === 0) {
            setError("Select at least one basket to create an AMS.");
            return;
        }
        if (!isValid) {
            const sum = Math.round(totalAllocation * 100) / 100;
            setError(`Allocations must total 100%. Current total is ${sum}%.`);
            return;
        }

        const parsedMinSip = parseFloat(minSipInput);
        const parsedMinLumpsum = parseFloat(minLumpsumInput);
        const parsedMinInvestMore = parseFloat(minInvestMoreInput);

        /* Admin overrides may raise the AMS minimum but must never drop below
         * the basket-derived requirement. Collect both violations so one toast
         * covers everything. */
        const violations = [];
        if (Number.isFinite(parsedMinSip) && parsedMinSip < minSipAmount) {
            violations.push(`SIP ₹${parsedMinSip.toLocaleString('en-IN')} is below the calculated minimum of ₹${minSipAmount.toLocaleString('en-IN')}`);
        }
        if (Number.isFinite(parsedMinLumpsum) && parsedMinLumpsum < minLumpsumAmount) {
            violations.push(`Lumpsum ₹${parsedMinLumpsum.toLocaleString('en-IN')} is below the calculated minimum of ₹${minLumpsumAmount.toLocaleString('en-IN')}`);
        }
        if (Number.isFinite(parsedMinInvestMore) && parsedMinInvestMore < minInvestMoreAmount) {
            violations.push(`Additional amount ₹${parsedMinInvestMore.toLocaleString('en-IN')} is below the calculated minimum of ₹${minInvestMoreAmount.toLocaleString('en-IN')}`);
        }
        if (violations.length > 0) {
            setError(`Minimum AMS amount invalid — ${violations.join("; ")}.`);
            return;
        }

        setLoader(true);

        /* Only the selected baskets are attached — the AMS is built dynamically
         * from this subset. */
        const payload = {
            id: isEdit ? props.data.id : null,
            name: name.trim(),
            ams_alias: amsAlias.trim() || null,
            min_sip: Number.isFinite(parsedMinSip) ? parsedMinSip : minSipAmount,
            min_lumpsum: Number.isFinite(parsedMinLumpsum) ? parsedMinLumpsum : minLumpsumAmount,
            min_invest_more: Number.isFinite(parsedMinInvestMore) ? parsedMinInvestMore : minInvestMoreAmount,
            allocations: selectedRows.map((r) => ({
                goal_id: r.basketId,
                allocation: parseFloat(r.allocation) || 0,
            })),
        };

        Promise.resolve(props.onSubmit && props.onSubmit(payload, isEdit))
            .catch((errMsg) => {
                if (typeof errMsg === "string") setError(errMsg);
            })
            .finally(() => {
                setLoader(false);
            });
    };

    return (
        <div className={`${styles.e_modal} modal fade`}
            id={MODAL_ID}
            tabIndex="-1"
            data-bs-backdrop="static"
            aria-labelledby="addAMSForm"
            aria-hidden="true">
            <div className={`${styles.e_modal_dialog} modal-dialog modal-dialog-centered`}>
                <div className={`modal-content ${styles.e_add_ams_modal_content}`}>
                    <div className="modal-header">
                        <h6>{isEdit ? "Edit Custom AMS" : "Create Custom AMS"}</h6>
                        <button type="button"
                            className="btn-close"
                            id="add-ams-form-close"
                            data-bs-dismiss="modal"
                            aria-label="Close"
                            disabled={loader} />
                        <button type="button"
                            className="d-none"
                            id="add-ams-form-dismiss"
                            data-bs-dismiss="modal"
                            aria-label="Close" />
                    </div>
                    <div className={`modal-body ${styles.e_add_ams_modal_body}`}>
                        <div className="row">
                            <div className="col-12 mb-4">
                                <label className={styles.e_field_label}>AMS name</label>
                                <input type="text"
                                    className={styles.e_modal_input}
                                    placeholder="Enter name"
                                    value={name}
                                    disabled={loader}
                                    maxLength={60}
                                    onChange={(e) => setName(e.target.value)} />
                            </div>

                            <div className="col-12 mb-4">
                                <label className={styles.e_field_label}>AMS Alias</label>
                                <input type="text"
                                    className={styles.e_modal_input}
                                    placeholder="Enter alias"
                                    value={amsAlias}
                                    disabled={loader}
                                    maxLength={120}
                                    onChange={(e) => setAmsAlias(e.target.value)} />
                            </div>

                            <div className="col-12 mb-2">
                                <span className={styles.e_section_label}>
                                    {isEdit ? "Basket Allocations" : "Selected basket allocations"}
                                </span>
                            </div>

                            <div className="col-12">
                                {rows.map((row) => (
                                        <div key={row.key} className="mb-3">
                                            <div className="row gx-2 align-items-center">
                                                <div className="col-lg-6 col-md-6 col-5">
                                                    <SelectBox
                                                        isSearchable={true}
                                                        isClearable={false}
                                                        options={_optionsForRow(row.key)}
                                                        value={_valueForRow(row)}
                                                        isDisabled={loader || allBaskets.length === 0}
                                                        placeholder={allBaskets.length === 0 ? "Loading baskets…" : "Search Basket"}
                                                        selectchange={(opt) => _handleRowSelect(row.key, opt)} />
                                                </div>
                                                <div className="col-lg-3 col-md-3 col-3">
                                                    <input
                                                        type="text"
                                                        inputMode="decimal"
                                                        className={`${styles.e_modal_input} ${styles.e_bordered_input} ${styles.e_allocation_input}`}
                                                        placeholder="0"
                                                        value={row.allocation ?? ""}
                                                        disabled={loader || row.basketId == null}
                                                        onChange={(e) => _handleAllocationChange(row.key, e.target.value)} />
                                                </div>
                                                <div className="col-lg-3 col-md-3 col-4">
                                                    <button type="button"
                                                        className={styles.e_row_delete}
                                                        disabled={loader}
                                                        onClick={() => _removeRow(row.key)}
                                                        aria-label="Delete basket">
                                                        <img src={DeleteIcon} alt="Delete" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                            </div>

                            <div className="col-12 mb-2">
                                <button type="button"
                                    className={styles.e_add_new}
                                    disabled={loader}
                                    onClick={_addRow}>
                                    <img src={AddNew} alt="" />
                                    <span>Add New</span>
                                </button>
                            </div>

                            <div className="col-12 mb-2 mt-2">
                                <span className={styles.e_section_label}>Minimum AMS amount required</span>
                            </div>
                            <div className="col-12 mb-3">
                                <div className={`row align-items-center ${styles.e_min_amount_row}`}>
                                    <div className="col-4">
                                        <label className={styles.e_field_label}>Minimum SIP amount (₹)</label>
                                        {minSipAmount > 0 && (
                                            <span className={styles.e_calc_min_hint}>
                                                Calculated minimum: ₹{minSipAmount.toLocaleString('en-IN')}
                                            </span>
                                        )}
                                    </div>
                                    <div className="col-8">
                                        <input type="text"
                                            inputMode="decimal"
                                            className={`${styles.e_modal_input} ${styles.e_bordered_input}`}
                                            value={minSipInput}
                                            disabled={loader}
                                            onChange={(e) => {
                                                const cleaned = e.target.value.replace(/[^0-9.]/g, "");
                                                setMinSipDirty(true);
                                                setMinSipInput(cleaned);
                                            }} />
                                    </div>
                                </div>
                                <div className={`row align-items-center ${styles.e_min_amount_row}`}>
                                    <div className="col-4">
                                        <label className={styles.e_field_label}>Minimum Lumpsum amount (₹)</label>
                                        {minLumpsumAmount > 0 && (
                                            <span className={styles.e_calc_min_hint}>
                                                Calculated minimum: ₹{minLumpsumAmount.toLocaleString('en-IN')}
                                            </span>
                                        )}
                                    </div>
                                    <div className="col-8">
                                        <input type="text"
                                            inputMode="decimal"
                                            className={`${styles.e_modal_input} ${styles.e_bordered_input}`}
                                            value={minLumpsumInput}
                                            disabled={loader}
                                            onChange={(e) => {
                                                const cleaned = e.target.value.replace(/[^0-9.]/g, "");
                                                setMinLumpsumDirty(true);
                                                setMinLumpsumInput(cleaned);
                                            }} />
                                    </div>
                                </div>
                                <div className={`row align-items-center ${styles.e_min_amount_row}`}>
                                    <div className="col-4">
                                        <label className={styles.e_field_label}>Minimum additional amount (₹)</label>
                                        {minInvestMoreAmount > 0 && (
                                            <span className={styles.e_calc_min_hint}>
                                                Calculated minimum: ₹{minInvestMoreAmount.toLocaleString('en-IN')}
                                            </span>
                                        )}
                                    </div>
                                    <div className="col-8">
                                        <input type="text"
                                            inputMode="decimal"
                                            className={`${styles.e_modal_input} ${styles.e_bordered_input}`}
                                            value={minInvestMoreInput}
                                            disabled={loader}
                                            onChange={(e) => {
                                                const cleaned = e.target.value.replace(/[^0-9.]/g, "");
                                                setMinInvestMoreDirty(true);
                                                setMinInvestMoreInput(cleaned);
                                            }} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className={`modal-footer ${styles.e_add_ams_modal_footer}`}>
                        <button type="button"
                            className={styles.e_modal_cancel}
                            data-bs-dismiss="modal"
                            disabled={loader}>
                            Cancel
                        </button>
                        <button type="button"
                            className={`${styles.e_modal_submit} ${loader ? "e-btn-loader" : ""}`}
                            disabled={!allFilled || loader}
                            onClick={_handleSubmit}>
                            {isEdit ? "Update AMS" : "Create AMS"}
                        </button>
                    </div>
                </div>
            </div>

            {error !== null ? <Toast data={error} type="error" id="add-ams-form-error" onClose={() => setError(null)} /> : null}
        </div>
    );
}
